import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Role } from '../../types';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import {
  BuildingOfficeIcon,
  UsersIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { PageTitle } from '../../components/ui/PageTitle';
import { SuperAdminService, SystemStats } from '../../services/superAdminService';
import { CompanyService, CompanyStats } from '../../services/companyService';
import logger from '../../utils/logger';

const SuperAdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [systemStats, setSystemStats] = useState<SystemStats | null>(null);
  const [companyStats, setCompanyStats] = useState<CompanyStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');

  const superAdminService = useMemo(() => new SuperAdminService(), []);
  const companyService = useMemo(() => new CompanyService(), []);

  const loadDashboardData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');

      logger.info('🔍 SuperAdminDashboard: データ取得開始');
      const [sysStats, compStats] = await Promise.all([
        superAdminService.getSystemStats(),
        companyService.getCompanyStats(),
      ]);

      logger.info('📊 SuperAdminDashboard: システム統計:', sysStats);
      logger.info('🏢 SuperAdminDashboard: 会社統計:', compStats);

      setSystemStats(sysStats);
      setCompanyStats(compStats);
    } catch (err) {
      logger.error('❌ Dashboard data loading failed:', err);
      setError('ダッシュボードデータの取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  }, []); // 依存配列を空にして無限ループを防ぐ

  useEffect(() => {
    logger.info('🔍 SuperAdminDashboard useEffect triggered:', { user, role: user?.role });
    if (user?.role === Role.SUPER_ADMIN) {
      logger.info('✅ super_admin権限確認済み、データ取得開始');
      loadDashboardData();
    } else {
      logger.info('❌ super_admin権限なし:', { role: user?.role, hasUser: !!user });
    }
  }, [user]); // loadDashboardDataを依存配列から削除

  // useEffectベースの権限チェックとリダイレクト制御
  useEffect(() => {
    logger.info('🔍 SuperAdminDashboard権限チェック:', { user, role: user?.role });
    if (user && user.role !== Role.SUPER_ADMIN) {
      logger.info('❌ リダイレクト実行: super_admin以外のユーザー');
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  // ユーザー情報がまだ読み込まれていない場合はローディング表示
  if (!user) {
    logger.info('⏳ ユーザー情報読み込み中...');
    return (
      <div className='min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600'></div>
      </div>
    );
  }

  // 権限確認：super_admin以外は表示しない
  if (user.role !== Role.SUPER_ADMIN) {
    logger.info('❌ 権限なし - 空画面表示');
    return <div>権限がありません</div>;
  }

  const getHealthStatusColor = (health: string) => {
    switch (health) {
      case 'healthy':
        return 'text-green-600';
      case 'warning':
        return 'text-yellow-600';
      case 'critical':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getHealthIcon = (health: string) => {
    switch (health) {
      case 'healthy':
        return <CheckCircleIcon className='h-6 w-6 text-green-600' />;
      case 'warning':
      case 'critical':
        return <ExclamationTriangleIcon className='h-6 w-6 text-yellow-600' />;
      default:
        return <ChartBarIcon className='h-6 w-6 text-gray-600' />;
    }
  };

  if (isLoading) {
    return (
      <div className='min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600'></div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gray-50 dark:bg-gray-900'>
      <PageTitle icon={ChartBarIcon} title='ラクハイ管理者ダッシュボード' />
      {/* ヘッダー */}
      <div className='bg-white dark:bg-gray-800 shadow'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='flex justify-between items-center py-6'>
            <div>
              <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
                システム全体の監視と陸送会社管理
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        {error && (
          <div className='mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4'>
            <div className='flex items-center'>
              <ExclamationTriangleIcon className='h-5 w-5 text-red-400 mr-2' />
              <p className='text-sm text-red-800 dark:text-red-200'>{error}</p>
            </div>
          </div>
        )}

        {/* システム統計カード */}
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8'>
          <div className='bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg'>
            <div className='p-5'>
              <div className='flex items-center'>
                <div className='flex-shrink-0'>
                  <BuildingOfficeIcon className='h-8 w-8 text-blue-600' />
                </div>
                <div className='ml-5 w-0 flex-1'>
                  <dl>
                    <dt className='text-sm font-medium text-gray-500 dark:text-gray-400 truncate'>
                      陸送会社総数
                    </dt>
                    <dd className='text-lg font-medium text-gray-900 dark:text-white'>
                      {systemStats?.totalCompanies || 0}
                    </dd>
                  </dl>
                </div>
              </div>
              <div className='mt-3'>
                <div className='text-sm text-gray-500 dark:text-gray-400'>
                  アクティブ: {systemStats?.activeCompanies || 0} 社
                </div>
              </div>
            </div>
          </div>

          <div className='bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg'>
            <div className='p-5'>
              <div className='flex items-center'>
                <div className='flex-shrink-0'>
                  <UsersIcon className='h-8 w-8 text-green-600' />
                </div>
                <div className='ml-5 w-0 flex-1'>
                  <dl>
                    <dt className='text-sm font-medium text-gray-500 dark:text-gray-400 truncate'>
                      ユーザー総数
                    </dt>
                    <dd className='text-lg font-medium text-gray-900 dark:text-white'>
                      {systemStats?.totalUsers || 0}
                    </dd>
                  </dl>
                </div>
              </div>
              <div className='mt-3'>
                <div className='text-sm text-gray-500 dark:text-gray-400'>
                  管理者: {systemStats?.totalAdmins || 0} 人
                </div>
              </div>
            </div>
          </div>

          <div className='bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg'>
            <div className='p-5'>
              <div className='flex items-center'>
                <div className='flex-shrink-0'>
                  <ChartBarIcon className='h-8 w-8 text-purple-600' />
                </div>
                <div className='ml-5 w-0 flex-1'>
                  <dl>
                    <dt className='text-sm font-medium text-gray-500 dark:text-gray-400 truncate'>
                      今月の新規登録
                    </dt>
                    <dd className='text-lg font-medium text-gray-900 dark:text-white'>
                      {companyStats?.companiesCreatedThisMonth || 0}
                    </dd>
                  </dl>
                </div>
              </div>
              <div className='mt-3'>
                <div className='text-sm text-green-600'>会社</div>
              </div>
            </div>
          </div>

          <div className='bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg'>
            <div className='p-5'>
              <div className='flex items-center'>
                <div className='flex-shrink-0'>
                  {getHealthIcon(systemStats?.systemHealth || 'healthy')}
                </div>
                <div className='ml-5 w-0 flex-1'>
                  <dl>
                    <dt className='text-sm font-medium text-gray-500 dark:text-gray-400 truncate'>
                      システム状態
                    </dt>
                    <dd
                      className={`text-lg font-medium ${getHealthStatusColor(systemStats?.systemHealth || 'healthy')}`}
                    >
                      {systemStats?.systemHealth === 'healthy' && '正常'}
                      {systemStats?.systemHealth === 'warning' && '注意'}
                      {systemStats?.systemHealth === 'critical' && '要対応'}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 最近のアクティビティ（プレースホルダー） */}
        <div className='bg-white dark:bg-gray-800 shadow rounded-lg'>
          <div className='px-6 py-4 border-b border-gray-200 dark:border-gray-700'>
            <h3 className='text-lg font-medium text-gray-900 dark:text-white'>
              最近のアクティビティ
            </h3>
          </div>
          <div className='p-6'>
            <div className='text-center text-gray-500 dark:text-gray-400 py-8'>
              <ChartBarIcon className='mx-auto h-12 w-12 text-gray-400 mb-4' />
              <p>アクティビティログの実装準備中</p>
              <p className='text-sm mt-2'>Phase 4で監査ログ機能と併せて実装予定</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
